<template>
    <PageHeader :crumbs="[{ label: $t('dashboard.sidebar.settings') }]" />

    <div class="tw-p-2 tw-space-y-4">
        <!-- Business / portal settings (hidden for now — branding uses bundled assets) -->
        <div v-if="false" class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-overflow-hidden">
            <div class="tw-p-4 tw-border-b tw-border-solid tw-bg-gray-50 tw-flex tw-items-center tw-gap-3">
                <div class="tw-p-2 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-bg-white">
                    <icon icon="solar:shop-2-bold-duotone" class="tw-text-xl tw-text-primary-500" />
                </div>
                <div>
                    <h2 class="tw-font-semibold tw-text-gray-700">{{ $t('portal.settings.business') }}</h2>
                    <p class="tw-text-xs tw-text-gray-400 tw-mt-0.5">{{ $t('portal.settings.business-subtitle') }}</p>
                </div>
            </div>

            <div class="tw-p-5">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField left-icon="solar:buildings-2-bold-duotone" v-model="form.business_name" :label="$t('portal.settings.business-name')" type="text" :error="err('business_name')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField left-icon="solar:shield-user-bold-duotone" v-model="form.portal_name" :label="$t('portal.settings.portal-name')" type="text" :error="err('portal_name')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.currency" :label="$t('portal.settings.currency')" type="text" :error="err('currency')" :help="$t('portal.settings.currency-help')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.timezone" :label="$t('portal.settings.timezone')" type="text" :error="err('timezone')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField left-icon="solar:letter-bold-duotone" v-model="form.admin_email" :label="$t('portal.settings.admin-email')" type="text" :error="err('admin_email')" />
                    </div>

                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormLabel>{{ $t('portal.settings.logo') }}</FormLabel>
                        <LogoUploader :src="preview.logo || current.logo_url" @pick="onFile('logo', $event)" @clear="clearFile('logo')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormLabel>{{ $t('portal.settings.portal-logo') }}</FormLabel>
                        <LogoUploader :src="preview.portal_logo || current.portal_logo_url" @pick="onFile('portal_logo', $event)" @clear="clearFile('portal_logo')" />
                    </div>
                </div>
            </div>

            <div class="tw-px-5 tw-py-4 tw-border-t tw-border-solid tw-bg-gray-50/60 tw-flex tw-justify-end">
                <a-button :disabled="savingSettings" :loading="savingSettings" left-icon="solar:diskette-bold-duotone" @click="saveSettings" class="tw-min-w-[150px]">{{ $t('portal.settings.save-changes') }}</a-button>
            </div>
        </div>

        <!-- Change password -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-overflow-hidden">
            <div class="tw-p-4 tw-border-b tw-border-solid tw-bg-gray-50 tw-flex tw-items-center tw-gap-3">
                <div class="tw-p-2 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-bg-white">
                    <icon icon="solar:lock-password-bold-duotone" class="tw-text-xl tw-text-primary-500" />
                </div>
                <div>
                    <h2 class="tw-font-semibold tw-text-gray-700">{{ $t('portal.settings.change-password') }}</h2>
                    <p class="tw-text-xs tw-text-gray-400 tw-mt-0.5">{{ $t('portal.settings.password-subtitle') }}</p>
                </div>
            </div>

            <div class="tw-p-5">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField left-icon="solar:lock-keyhole-bold-duotone" v-model="pw.current_password" :label="$t('portal.settings.current-password')" type="password" :error="pwErr('current_password')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField left-icon="solar:lock-keyhole-unlocked-bold-duotone" v-model="pw.password" :label="$t('portal.settings.new-password')" type="password" :error="pwErr('password')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField left-icon="solar:lock-keyhole-unlocked-bold-duotone" v-model="pw.password_confirmation" :label="$t('portal.settings.confirm-password')" type="password" :error="pwErr('password_confirmation')" />
                    </div>
                </div>
            </div>

            <div class="tw-px-5 tw-py-4 tw-border-t tw-border-solid tw-bg-gray-50/60 tw-flex tw-justify-end">
                <a-button :disabled="savingPw" :loading="savingPw" intent="secondary" left-icon="solar:key-bold-duotone" @click="savePassword" class="tw-min-w-[170px]">{{ $t('portal.settings.change-password') }}</a-button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import FormLabel from '@/components/form/FormLabel';
import PageHeader from '@/components/PageHeader.vue';
import LogoUploader from '@/components/form/LogoUploader.vue';
import { show, update, updatePassword } from '@/api/settings';
import { useSettingsStore } from '@/stores';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const i18n = useI18n();
const settingsStore = useSettingsStore();

const form = ref({ business_name: '', portal_name: '', currency: '', timezone: '', admin_email: '' });
const files = ref({ logo: null, portal_logo: null });
const preview = ref({ logo: null, portal_logo: null });
const current = ref({ logo_url: null, portal_logo_url: null });
const errors = ref({});
const pwErrors = ref({});
const savingSettings = ref(false);
const savingPw = ref(false);

const pw = ref({ current_password: '', password: '', password_confirmation: '' });

const err = useValidation(errors);
const pwErr = useValidation(pwErrors);

async function load() {
    try {
        const { data } = await show();
        const s = data.settings || {};
        form.value = {
            business_name: s.business_name || '',
            portal_name: s.portal_name || '',
            currency: s.currency || '',
            timezone: s.timezone || '',
            admin_email: s.admin_email || '',
        };
        current.value = { logo_url: s.logo_url, portal_logo_url: s.portal_logo_url };
    } catch (e) { /* interceptor */ }
}

function onFile(key, file) {
    files.value[key] = file;
    preview.value[key] = URL.createObjectURL(file);
}

function clearFile(key) {
    files.value[key] = null;
    preview.value[key] = null;
}

async function saveSettings() {
    savingSettings.value = true;
    errors.value = {};
    try {
        const fd = new FormData();
        Object.entries(form.value).forEach(([k, v]) => fd.append(k, v ?? ''));
        if (files.value.logo) fd.append('logo', files.value.logo);
        if (files.value.portal_logo) fd.append('portal_logo', files.value.portal_logo);

        const { data } = await update(fd);
        if (data.code === 'SUCCESS') {
            const s = data.settings || {};
            current.value = { logo_url: s.logo_url, portal_logo_url: s.portal_logo_url };
            preview.value = { logo: null, portal_logo: null };
            files.value = { logo: null, portal_logo: null };
            settingsStore.setBranding({
                business_name: s.business_name,
                portal_name: s.portal_name,
                currency: s.currency,
                timezone: s.timezone,
                logo_url: s.logo_url,
                portal_logo_url: s.portal_logo_url,
            });
            useAlert(i18n.t('messages.updated-successfully', { field: i18n.t('dashboard.sidebar.settings') }));
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        savingSettings.value = false;
    }
}

async function savePassword() {
    savingPw.value = true;
    pwErrors.value = {};
    try {
        const { data } = await updatePassword(pw.value);
        if (data.code === 'SUCCESS') {
            useAlert(i18n.t('portal.settings.password-changed'));
            pw.value = { current_password: '', password: '', password_confirmation: '' };
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) pwErrors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        savingPw.value = false;
    }
}

load();
</script>

<style scoped>
</style>
